import React, { useState } from 'react';
import { X, Pencil, Trash2, Sparkles, CheckCircle2, Circle, Loader, Link2 } from 'lucide-react';
import CircularProgress from './CircularProgress';
import { categoryMeta, PRIORITIES } from './Goals';
import {
  analyzeGoal, statusMeta, buildMilestones, requiredMonthly, yearsUntil, formatCurrency,
} from '../lib/goalMath';

const dateFromYears = (yrs) => {
  const d = new Date();
  d.setMonth(d.getMonth() + Math.round(yrs * 12));
  return d.toISOString().split('T')[0];
};

const healthColor = (h) => (h >= 75 ? 'text-success-600' : h >= 50 ? 'text-warning-600' : 'text-danger-600');

const GoalDetail = ({ goal, currentValue, assets = [], onClose, onEdit, onDelete, onApply }) => {
  const base = analyzeGoal(goal, currentValue);

  // What-if draft state
  const [whatIf, setWhatIf] = useState({
    years: Math.max(1, yearsUntil(goal.target_date) || 10),
    monthly: base.monthly,
    expectedReturn: base.annualReturn,
  });

  const draftGoal = {
    ...goal,
    monthly_contribution: whatIf.monthly,
    expected_return: whatIf.expectedReturn,
    target_date: dateFromYears(whatIf.years),
  };
  const sim = analyzeGoal(draftGoal, currentValue);
  const dirty =
    Math.round(whatIf.monthly) !== Math.round(base.monthly) ||
    Number(whatIf.expectedReturn) !== Number(base.annualReturn) ||
    whatIf.years !== Math.max(1, yearsUntil(goal.target_date) || 10);

  const { Icon, label } = categoryMeta(goal.category);
  const prio = PRIORITIES.find((p) => p.value === goal.priority) || PRIORITIES[2];
  const sm = statusMeta[sim.status];
  const milestones = buildMilestones(base.target, currentValue);

  const linkedAssets = assets.filter((a) => (goal.linked_asset_ids || []).includes(a.id));

  // Action center recommendations (based on the saved plan)
  const recs = [];
  if (base.requiredMo > base.monthly + 1) {
    recs.push(`Increase monthly savings by ${formatCurrency(base.requiredMo - base.monthly)} to ${formatCurrency(base.requiredMo)}/mo.`);
  }
  const delayedRequired = requiredMonthly(base.current, base.target, base.annualReturn, base.months + 12);
  if (base.requiredMo > base.monthly + 1 && delayedRequired < base.requiredMo) {
    recs.push(`Delay the target date 1 year to lower the required savings to ${formatCurrency(delayedRequired)}/mo.`);
  }
  const idleCash = assets
    .filter((a) => a.category === 'cash' && !(goal.linked_asset_ids || []).includes(a.id))
    .reduce((s, a) => s + (parseFloat(a.value) || 0), 0);
  if (idleCash > 1000 && base.gap > 0) {
    recs.push(`Reallocate part of your ${formatCurrency(idleCash)} in idle cash toward this goal.`);
  }
  if (base.annualReturn < 5 && base.status !== 'on') {
    recs.push(`A modestly higher expected return could close the gap — review your allocation.`);
  }
  if (recs.length === 0) {
    recs.push(`You're on track — consider raising the target or starting a new goal.`);
  }

  const applyWhatIf = () => {
    onApply({
      monthly_contribution: Math.round(whatIf.monthly),
      expected_return: Number(whatIf.expectedReturn),
      target_date: dateFromYears(whatIf.years),
    });
    onClose();
  };

  const monthlyMax = Math.max(2000, Math.ceil((base.requiredMo * 1.5) / 100) * 100, base.monthly * 2);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gray-50 rounded-lg shadow-xl max-w-4xl w-full my-8">
        {/* Header */}
        <div className="bg-white rounded-t-lg px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-lg bg-primary-50 flex items-center justify-center">
              <Icon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-900">{goal.name}</h3>
                <span className={prio.badge}>{prio.label}</span>
              </div>
              <p className="text-xs text-gray-500">{label}{goal.target_date ? ` · Target ${new Date(goal.target_date).toLocaleDateString()}` : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onEdit} className="btn-outline btn-sm"><Pencil className="h-4 w-4" /></button>
            <button onClick={onDelete} className="btn-outline btn-sm text-danger-600"><Trash2 className="h-4 w-4" /></button>
            <button onClick={onClose} className="btn-outline btn-sm"><X className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Top: progress + gap + health */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Progress ring */}
            <div className="card p-6 flex flex-col items-center justify-center">
              <CircularProgress
                value={base.progressPct}
                size={150}
                stroke={12}
                color={statusMeta[base.status].ring}
                label={`${Math.round(base.progressPct)}%`}
                sublabel="complete"
              />
              <div className="mt-4 w-full space-y-1.5 text-sm">
                <div className="flex justify-between"><span className="text-gray-500">Current</span><span className="font-medium">{formatCurrency(base.current)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Target</span><span className="font-medium">{formatCurrency(base.target)}</span></div>
                <div className="flex justify-between border-t border-gray-100 pt-1.5"><span className="text-gray-500">Funding gap</span><span className="font-semibold text-danger-600">{formatCurrency(base.gap)}</span></div>
              </div>
            </div>

            {/* Recommendation + success meter */}
            <div className="card p-6 lg:col-span-2 space-y-5">
              <div>
                <p className="text-sm font-medium text-gray-600">Recommended monthly savings</p>
                <p className="text-3xl font-bold text-primary-600 mt-1">{formatCurrency(base.requiredMo)}<span className="text-base font-medium text-gray-400">/mo</span></p>
                <p className="text-xs text-gray-500 mt-1">
                  You're currently saving {formatCurrency(base.monthly)}/mo at {base.annualReturn}% expected return · {base.months} months to target.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-medium text-gray-600">Success probability</p>
                  <span className={`text-sm font-semibold ${statusMeta[base.status].color}`}>{base.probability}% · {statusMeta[base.status].label}</span>
                </div>
                <div className="h-3 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${base.probability}%`, backgroundColor: statusMeta[base.status].ring }} />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-1">
                <div className="text-center">
                  <p className={`text-3xl font-bold ${healthColor(base.health)}`}>{base.health}</p>
                  <p className="text-xs text-gray-500">Health Score / 100</p>
                </div>
                <div className="text-xs text-gray-500 border-l border-gray-200 pl-4">
                  A blend of your progress, savings rate, and probability of success. Higher is healthier.
                </div>
              </div>
            </div>
          </div>

          {/* What-if planning */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary-600" /> What-if planning</h4>
              {dirty && (
                <div className="flex items-center gap-2">
                  <button onClick={() => setWhatIf({ years: Math.max(1, yearsUntil(goal.target_date) || 10), monthly: base.monthly, expectedReturn: base.annualReturn })} className="btn-outline btn-sm">Reset</button>
                  <button onClick={applyWhatIf} className="btn-primary btn-sm">Apply changes</button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Slider label="Years to goal" value={whatIf.years} min={1} max={40} step={1}
                display={`${whatIf.years} yr`}
                onChange={(v) => setWhatIf({ ...whatIf, years: v })} />
              <Slider label="Monthly savings" value={whatIf.monthly} min={0} max={monthlyMax} step={50}
                display={formatCurrency(whatIf.monthly)}
                onChange={(v) => setWhatIf({ ...whatIf, monthly: v })} />
              <Slider label="Expected return" value={whatIf.expectedReturn} min={1} max={10} step={0.5}
                display={`${whatIf.expectedReturn}%`}
                onChange={(v) => setWhatIf({ ...whatIf, expectedReturn: v })} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-5 border-t border-gray-100">
              <Stat label="Projected value" value={formatCurrency(sim.projected)} />
              <Stat label="vs Target" value={`${Math.round((sim.projected / (sim.target || 1)) * 100)}%`} />
              <Stat label="Success" value={`${sim.probability}%`} valueClass={sm.color} />
              <Stat label="Health" value={`${sim.health}/100`} valueClass={healthColor(sim.health)} />
            </div>
          </div>

          {/* Milestones + linked + notes/actions */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Milestones */}
            <div className="card p-6">
              <h4 className="font-semibold text-gray-900 mb-4">Milestones</h4>
              <div className="space-y-3">
                {milestones.map((m, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {m.achieved ? <CheckCircle2 className="h-5 w-5 text-success-600 shrink-0" />
                      : m.inProgress ? <Loader className="h-5 w-5 text-warning-600 shrink-0" />
                      : <Circle className="h-5 w-5 text-gray-300 shrink-0" />}
                    <div className="flex-1 flex items-center justify-between">
                      <span className={`text-sm ${m.achieved ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{formatCurrency(m.amount)} <span className="text-gray-400">({m.pct}%)</span></span>
                      <span className="text-xs text-gray-400">{m.achieved ? 'Reached' : m.inProgress ? 'In progress' : 'Upcoming'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Action center */}
            <div className="card p-6">
              <h4 className="font-semibold text-gray-900 mb-4">To improve success</h4>
              <ul className="space-y-2.5">
                {recs.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <CheckCircle2 className="h-4 w-4 text-primary-500 mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Linked accounts */}
          {linkedAssets.length > 0 && (
            <div className="card p-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2"><Link2 className="h-4 w-4 text-gray-400" /> Linked accounts</h4>
              <div className="divide-y divide-gray-100">
                {linkedAssets.map((a) => (
                  <div key={a.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-gray-700">{a.name}</span>
                    <span className="font-medium text-gray-900">{formatCurrency(a.value)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between py-2 text-sm font-semibold">
                  <span className="text-gray-900">Total linked</span>
                  <span className="text-success-600">{formatCurrency(base.current)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {goal.notes && (
            <div className="card p-6">
              <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
              <p className="text-sm text-gray-600 whitespace-pre-line">{goal.notes}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Slider = ({ label, value, min, max, step, display, onChange }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-sm font-medium text-gray-600">{label}</label>
      <span className="text-sm font-semibold text-gray-900">{display}</span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full accent-primary-600"
    />
  </div>
);

const Stat = ({ label, value, valueClass = 'text-gray-900' }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-lg font-bold ${valueClass}`}>{value}</p>
  </div>
);

export default GoalDetail;
